import React, { useState, useEffect } from 'react'
import DashboardLayout from '../dashboard/layout'
import { useTranslation } from 'react-i18next'
import CookieManager from '../lib/CookiesManager'
import { getUser, updateUser } from '../../services/oneUser'
import { FaCheckCircle, FaClock, FaTimesCircle } from 'react-icons/fa'
import UploadVerificationImage from '../components/UploadVerificationImage'
import { fetchImages } from '../../services/gallery'
import {
  Shield,
  CreditCard,
  FileCheck,
  Camera,
  BadgeCheck,
  Users,
  Lock,
} from 'lucide-react'
import { X_API_KEY } from '../../services/api'
import ImageWithApiKey from '../components/ImageWithApiKey'

const VerifiedStatusPage: React.FC = () => {
  const { t } = useTranslation()
  const [verifiedStatus, setVerifiedStatus] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null)
  const [verificationImage, setVerificationImage] = useState<string | null>(
    null,
  )
  const [error, setError] = useState<string | null>(null)

  const userId = CookieManager.getUserId()
  const token = CookieManager.getToken()

  const updateVerificationStatus = async (currentUserData: any) => {
    try {
      const updateData = {
        ...currentUserData.user,
        verifiedStatus: 'pending',
        bannedReason: {
          title: 'none',
          description: 'none',
        },
      }

      if (userId && token) {
        await updateUser(userId, token, updateData)
      } else {
        throw new Error('User ID or token is missing')
      }
      setVerifiedStatus('pending')
      setError(null)
    } catch (error) {
      console.error('Error updating verification status:', error)
      setError(error instanceof Error ? error.message : 'Update failed')
    }
  }

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId || !token) {
        setIsLoading(false)
        return
      }

      try {
        const userData = await getUser(userId, token)
        setVerifiedStatus(userData.user.verifiedStatus)

        const galleryImages = await fetchImages(userId, token)
        const verificationImg = galleryImages.find(img =>
          img.fileName.startsWith('(verification-'),
        )

        if (verificationImg) {
          setVerificationImage(verificationImg.url)
        }

        setIsLoading(false)
      } catch (error) {
        console.error('Error in fetchUserData:', error)
        setError(error instanceof Error ? error.message : 'Error fetching data')
        setIsLoading(false)
      }
    }

    fetchUserData()
  }, [userId, token])

  const handleUploadSuccess = async (imageUrl: string) => {
    if (!userId || !token) return

    try {
      const userData = await getUser(userId, token)
      await updateVerificationStatus(userData)
      setVerificationImage(imageUrl)
    } catch (error) {
      console.error('Error updating user status:', error)
      setError(error instanceof Error ? error.message : 'Update failed')
    }
  }

  const renderStatusSection = () => {
    switch (verifiedStatus) {
      case 'verified':
        return (
          <section className='bg-grisclair rounded-lg p-4 sm:p-6'>
            <h2 className='text-lg sm:text-xl font-semibold text-anthracite mb-2'>
              {t('current-status')}
            </h2>
            <h2 className='text-lg flex sm:text-xl font-semibold text-anthracite mb-2'>
              <FaCheckCircle className='w-4 h-4 sm:w-5 sm:h-5 mr-2 text-whatsapp' />
              <span className='text-sm sm:text-base'>{t('verified')}</span>
            </h2>
            <p className='text-sm sm:text-base text-gray-700 mb-4'>
              {t('verified-profile-message')}
            </p>
          </section>
        )
      case 'pending':
        return (
          <section className='bg-grisclair rounded-lg p-4 sm:p-6'>
            <h2 className='text-lg sm:text-xl font-semibold text-anthracite mb-2'>
              {t('current-status')}
            </h2>
            <h2 className='text-lg flex sm:text-xl font-semibold text-anthracite mb-2'>
              <FaClock className='w-4 h-4 sm:w-5 sm:h-5 mr-2 text-orange-500' />
              <span className='text-sm sm:text-base'>
                {t('verification-pending')}
              </span>
            </h2>
            <p className='text-sm sm:text-base text-gray-700 mb-4'>
              {t('verification-pending-message')}
            </p>
            {verificationImage && (
              <>
                <ImageWithApiKey
                  imageUrl={verificationImage}
                  apiKey={X_API_KEY}
                />
              </>
            )}
          </section>
        )
      case 'denied':
        return (
          <section className='bg-grisclair rounded-lg p-4 sm:p-6'>
            <h2 className='text-lg sm:text-xl font-semibold text-anthracite mb-2'>
              {t('current-status')}
            </h2>

            <h2 className='text-lg flex sm:text-xl font-semibold text-anthracite mb-2'>
              <FaTimesCircle className='w-4 h-4 sm:w-5 sm:h-5 mr-2 text-red-500' />
              <span className='text-sm sm:text-base'>
                {t('status-denied-desc')}
              </span>
            </h2>
            <p className='text-sm sm:text-base text-gray-700 mb-4'>
              {t('unverified-profile-message')}
            </p>
            {userId && token ? (
              <UploadVerificationImage
                userId={userId}
                token={token}
                onUploadSuccess={handleUploadSuccess}
                existingImage={verificationImage}
              />
            ) : (
              <p className='text-sm sm:text-base text-gray-700'>
                {t('login-to-upload')}
              </p>
            )}
          </section>
        )
      default:
        return (
          <section className='bg-grisclair rounded-lg p-4 sm:p-6'>
            <h2 className='text-lg sm:text-xl font-semibold text-anthracite mb-2'>
              {t('current-status')}
            </h2>
            <h2 className='text-lg flex sm:text-xl font-semibold text-anthracite mb-2'>
              <FaCheckCircle className='w-4 h-4 sm:w-5 sm:h-5 mr-2 text-red-500' />
              <span className='text-sm sm:text-base'>
                {t('status-not-verified')}
              </span>
            </h2>
            <p className='text-sm sm:text-base text-gray-700 mb-4'>
              {t('unverified-profile-message')}
            </p>
            {userId && token ? (
              <UploadVerificationImage
                userId={userId}
                token={token}
                onUploadSuccess={handleUploadSuccess}
                existingImage={verificationImage}
              />
            ) : (
              <p className='text-sm sm:text-base text-gray-700'>
                {t('login-to-upload')}
              </p>
            )}
          </section>
        )
    }
  }

  return (
    <DashboardLayout title={t('dashboard.verified-status')}>
      <div className='px-0 py-2 mx-auto'>
        <div className='space-y-2'>
          {error && (
            <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center'>
              <Shield className='w-5 h-5 mr-2 flex-shrink-0' />
              <span className='text-sm sm:text-base'>{error}</span>
            </div>
          )}
          {renderStatusSection()}

          {verifiedStatus !== 'verified' && (
            <section className='bg-grisclair rounded-lg p-4 sm:p-6'>
              <div className='flex items-center mb-4'>
                <FileCheck className='w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-blue-600 flex-shrink-0' />
                <h2 className='text-base sm:text-xl font-semibold text-anthracite'>
                  {t('accepted-documents')}
                </h2>
              </div>
              <div className='sm:flex sm:gap-4 sm:items-start'>
                <img
                  className='rounded-lg object-cover shadow-lg w-full sm:w-52 h-auto mb-4 sm:mb-0'
                  src='/images/verify-picture-example.jpg'
                  alt='sexagogo - Verify Picture Example'
                />
                <ul className='space-y-3 flex-grow'>
                  <li className='flex items-center text-sm sm:text-base text-gray-700'>
                    <CreditCard className='w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 text-gray-500 flex-shrink-0' />
                    <span>{t('id-card')}</span>
                  </li>
                  <li className='flex items-center text-sm sm:text-base text-gray-700'>
                    <FileCheck className='w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 text-gray-500 flex-shrink-0' />
                    <span>{t('passport')}</span>
                  </li>
                  <li className='flex items-center text-sm sm:text-base text-gray-700'>
                    <BadgeCheck className='w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 text-gray-500 flex-shrink-0' />
                    <span>{t('driver-license')}</span>
                  </li>
                  <li className='flex items-center text-sm sm:text-base text-gray-700'>
                    <Camera className='w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 text-gray-500 flex-shrink-0' />
                    <span>{t('photo-with-sexagogo')}</span>
                  </li>
                </ul>
              </div>
            </section>
          )}

          <section className='bg-grisclair rounded-lg p-4 sm:p-6'>
            <div className='flex items-center mb-4'>
              <Users className='w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-green-600 flex-shrink-0' />
              <h2 className='text-base sm:text-xl font-semibold text-anthracite'>
                {t('verified-label-advantages')}
              </h2>
            </div>
            <p className='text-sm sm:text-base text-gray-700 leading-relaxed'>
              {t('verified-label-description', { visitors: '40%' })}
            </p>
          </section>

          <section className='bg-redlight rounded-lg p-4 sm:p-6'>
            <div className='flex items-center mb-4'>
              <Lock className='w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-red-600 flex-shrink-0' />
              <h2 className='text-base sm:text-xl font-semibold text-anthracite'>
                {t('privacy-protection')}
              </h2>
            </div>
            <p className='text-sm sm:text-base text-gray-700 leading-relaxed'>
              {t('verification-photos-privacy')}
            </p>
          </section>
        </div>
      </div>
    </DashboardLayout>
  )
}

export default VerifiedStatusPage
